import type { ProductPlan } from "@/features/products/types"
import { formatMoney } from "@/shared/lib/money"
import { isTrialPlan, planPeriodLabel, savingsPercent, strikePrice } from "../domain/pricing"
import { SavingsBadge } from "./savings-badge"

interface PlanSummaryProps {
  plan: ProductPlan
}

/**
 * Single-plan summary.
 * - Trial plan: a "{N}-Day Trial" row (recurring price struck through + trial price)
 *   plus the "Total due today" line.
 * - Non-trial plan: just the "Total due today" line (no trial row / divider).
 */
export function PlanSummary({ plan }: PlanSummaryProps) {
  const isTrial = isTrialPlan(plan)
  const savings = savingsPercent(plan)
  const price = formatMoney(plan.price, plan.currency)
  const strike = strikePrice(plan)

  return (
    <div className="rounded-2xl bg-purple-light/10 p-4">
      {isTrial && (
        <>
          <div className="flex items-center justify-between gap-3">
            <span className="text-base font-semibold text-purple-light/60">
              {planPeriodLabel(plan)}
            </span>
            <div className="flex items-center gap-2">
              {strike != null && (
                <span className="text-base text-purple-light/40 line-through">
                  {formatMoney(strike, plan.currency)}
                </span>
              )}
              <span className="text-base font-semibold text-white">{price}</span>
            </div>
          </div>
          <div className="my-3 h-px w-full bg-purple-light/10" />
        </>
      )}

      <div className="flex items-center justify-between gap-3">
        <span className="text-base font-semibold text-purple-light">Total due today:</span>
        <div className="flex items-center gap-2">
          <span className="text-base font-semibold text-white">{price}</span>
          {savings > 0 && <SavingsBadge percent={savings} />}
        </div>
      </div>
    </div>
  )
}
